import React, {
    useMemo,
    useState,
    useEffect,
    useCallback,
    createRef,
} from 'react';
import dayjs from 'utils/dayjs-timezone';
import dayjsDuration from 'utils/dayjs-duration';
import PropTypes from 'prop-types';
import {
    Button,
    Flex,
    Text,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Alert,
    Icon,
    Image,
    Loader,
    useToast,
} from '@spothero/ui';
import IconInfoCircle from '@spothero/icons/info-circle-filled';
import FacilityUtils from 'utils/facility-utils';
import FormatUtils from 'utils/format';
import ReservationUtils from 'utils/reservation-utils';
import {useSelector, useDispatch} from 'react-redux';
import {PROPTYPE_RESERVATION} from '@/proptypes/reservation-prop-types';
import {useForm} from 'react-hook-form';
import {
    refundReservation,
    toggleModal,
} from 'store/parking-pass/parking-pass-actions';
import useConfig from 'hooks/use-config';
import {REFUND} from 'store/parking-pass/parking-pass-reducer';
import ReservationAPI from 'api/reservation';
import ReservationRefund, {
    REFUND_CREDIT_VALUE,
    REFUND_COMBINATION_VALUE,
} from '../reservation-refund';
import RefundReasons from '../refund-reasons';
import trackCancellationRequest from 'segment/events/cancellation-request';
import trackCancellationFailed from 'segment/events/cancellation-failed';
import trackReservationRefunded from 'segment/events/reservation-refunded';
import UserUtils from 'utils/user-utils';

// Forms and fields
const REFUND_REASON_FIELD = 'reason';
const REFUND_REASON_FORM = 'reasonForm';
const REFUND_DESTINATION_FIELD = 'destination';
const REFUND_DESTINATION_FORM = 'destinationForm';
// Views
const LOADING_VIEW = 'LOADING_VIEW';
const INELIGIBLE_VIEW = 'INELIGIBLE_VIEW';
const REASON_SELECT_VIEW = 'REASON_SELECT_VIEW';
const REFUND_DESTINATION_VIEW = 'REFUND_DESTINATION_VIEW';

/* eslint-disable camelcase */
const CancelReservation = ({activeStatus, reservation, onTrackedClick}) => {
    const [openCancelInfoModal, setOpenCancelInfoModal] = useState(false);
    const dispatch = useDispatch();
    const {data: user} = useSelector(state => state.user);
    const {accessKey} = useSelector(store => store.parkingPass);
    const [currentView, setCurrentView] = useState(LOADING_VIEW);
    const [refundReason, setRefundReason] = useState(null);
    const isOpen = useSelector(state => state.parkingPass.modals[REFUND]);
    const {
        spotheroCreditUsed,
        priceBreakdown: {totalPrice},
    } = reservation;

    const handleToggleModal = useCallback(() => {
        dispatch(toggleModal({modal: REFUND}));
    }, [dispatch]);

    const checkEligibility = useCallback(async () => {
        setCurrentView(LOADING_VIEW);

        try {
            const {
                originalSourceRefundEligible,
                creditOnlyRefundEligible,
            } = await ReservationAPI.getRefundEligibility({
                reservationId: reservation.rentalId,
                accessKey,
            });

            if (!(originalSourceRefundEligible || creditOnlyRefundEligible)) {
                setCurrentView(INELIGIBLE_VIEW);
            } else {
                setCurrentView(REASON_SELECT_VIEW);
            }
        } catch {
            setCurrentView(INELIGIBLE_VIEW);
        }
    }, [reservation.rentalId, accessKey]);

    // if changeReservation param in the URL opens the modal, we still want to check eligibility
    useEffect(() => {
        if (isOpen) {
            checkEligibility();
        }
    }, [isOpen, checkEligibility]);

    const [refundError, setRefundError] = useState(false);
    const {siteUrl} = useConfig();
    const editLink = `${siteUrl}/account-reservations#current-${reservation.rentalId}-change`;
    const toast = useToast();

    const addToast = () => {
        const toastRef = createRef();

        const close = () => {
            if (toastRef.current) {
                toast.close(toastRef.current);
            }
        };

        toastRef.current = toast({
            position: 'top',
            duration: 3000,
            render: () => (
                <Alert
                    title="Reservation cancelled"
                    status="success"
                    showIcon
                    showDismiss
                    onDismissClick={close}
                />
            ),
        });
    };
    // Do not allow refund to credit if the user is not logged in or if they purchased through a partner (e.g. TicketMaster)
    const onlyOriginalPayment =
        user?.status !== UserUtils.AUTH_STATE.USER ||
        !reservation.canRefundAsSpotheroCredit;
    const onlyCredit =
        spotheroCreditUsed > 0 && spotheroCreditUsed === totalPrice;
    const isComboRefund =
        spotheroCreditUsed > 0 && spotheroCreditUsed < totalPrice;

    const {
        control: refundReasonControl,
        handleSubmit: handleRefundReasonSubmit,
        formState: {
            isSubmitting: refundReasonIsSubmitting,
            dirtyFields: refundReasonDirtyFields,
        },
        reset: refundReasonReset,
    } = useForm();

    const {
        control: refundDestinationControl,
        handleSubmit: handleRefundDestinationSubmit,
        formState: {
            isSubmitting: refundDestinationIsSubmitting,
            dirtyFields: refundDestinationDirtyFields,
        },
        reset: refundDestinationReset,
        register: refundDestinationRegister,
    } = useForm();

    const handleClose = () => {
        handleToggleModal();
        setTimeout(() => {
            refundReasonReset();
            refundDestinationReset();
            setRefundError(false);
        }, 100);
    };

    const onReasonSubmit = ({reason}) => {
        setRefundError(false);
        setRefundReason(reason);
        setCurrentView(REFUND_DESTINATION_VIEW);
    };

    const onDestinationSubmit = async ({destination}) => {
        const refundDestination =
            isComboRefund && destination !== REFUND_CREDIT_VALUE
                ? REFUND_COMBINATION_VALUE
                : destination;

        setRefundError(false);
        trackCancellationRequest({
            reservation,
            user,
            activeStatus,
            refundReason,
            refundDestination,
        });
        const response = await dispatch(
            refundReservation({
                reason: refundReason,
                refundAsCredit: destination === REFUND_CREDIT_VALUE,
            })
        );

        if (response.success) {
            handleClose();
            addToast();
            trackReservationRefunded({
                reservation,
                user,
                activeStatus,
                refundReason,
                refundDestination,
            });
        } else {
            setRefundError(true);
            trackCancellationFailed({
                reservation,
                user,
                activeStatus,
                refundReason,
                refundDestination,
            });
        }
    };

    const isPastCancellationMinutes = useMemo(() => {
        const currentTime = dayjs.utc(dayjs()).tz(reservation.timezone);

        return (
            reservation.starts.subtract(
                reservation.cancellationMinutes,
                'minutes'
            ) < currentTime
        );
    }, [
        reservation.starts,
        reservation.cancellationMinutes,
        reservation.timezone,
    ]);

    if (
        (isPastCancellationMinutes && reservation.cancellationMinutes <= 0) ||
        reservation.cancellationAllowed ===
            FacilityUtils.CANCELLATION_ALLOWED_ENUM.NOT_ALLOWED ||
        activeStatus !== ReservationUtils.ACTIVE_STATUS.NOT_STARTED ||
        reservation.entryTimestamp ||
        reservation.paymentStatus ===
            ReservationUtils.PAYMENT_STATUSES.REFUND ||
        (reservation.cancellationAllowed ===
            FacilityUtils.CANCELLATION_ALLOWED_ENUM.HERO &&
            !user.isAdmin) ||
        (reservation.reservationType ===
            ReservationUtils.RESERVATION_TYPES.MONTHLY &&
            reservation.monthlyRecurring)
    ) {
        return null;
    }

    const cancellationThresholdInHours = dayjsDuration
        .duration(reservation.cancellationMinutes, 'minutes')
        .asHours();
    const hoursText = `${dayjsDuration
        .duration(reservation.cancellationMinutes, 'minutes')
        .asHours()} ${FormatUtils.pluralize(
        cancellationThresholdInHours,
        'hour',
        'hours'
    )}`;

    return (
        <>
            <Flex
                marginBottom={{base: 5, tablet: 4}}
                justifyContent="center"
                borderColor="gray.100"
                borderTopWidth="1px"
                paddingTop={{base: 5, tablet: 4}}
                sx={{
                    '@media print': {
                        display: 'none',
                    },
                }}
            >
                {reservation.reservationType !==
                ReservationUtils.RESERVATION_TYPES.MONTHLY ? (
                    <>
                        {!isPastCancellationMinutes ? (
                            <Button
                                onClick={() => {
                                    onTrackedClick({
                                        clickTarget: 'Cancel Reservation',
                                        isReservationChange: true,
                                    });
                                    handleToggleModal();
                                }}
                                variant="tertiary"
                                fontWeight="semibold"
                                lineHeight={1.4}
                                data-testid="CancelReservation-button-delete"
                            >
                                Request Cancellation
                            </Button>
                        ) : (
                            <Text
                                fontWeight="normal"
                                color="gray.dark"
                                width="440px"
                                px={4}
                            >
                                Unfortunately, your time to cancel the
                                reservation has passed.
                            </Text>
                        )}
                        {!isPastCancellationMinutes &&
                            reservation.cancellationMinutes > 0 && (
                                <Button
                                    variant="tertiary"
                                    paddingLeft="1.5"
                                    color="gray.dark"
                                    onClick={() => setOpenCancelInfoModal(true)}
                                >
                                    <Icon
                                        alt="Info Circle Icon"
                                        as={IconInfoCircle}
                                        fontSize="sm"
                                    />
                                </Button>
                            )}
                    </>
                ) : (
                    <Button
                        onClick={() =>
                            onTrackedClick({
                                clickTarget: 'Cancel Reservation',
                                isReservationChange: true,
                            })
                        }
                        as="a"
                        href={editLink}
                        variant="tertiary"
                        fontWeight="semibold"
                        lineHeight={1.4}
                        data-testid="CancelReservation-button-delete"
                    >
                        Request Cancellation
                    </Button>
                )}
            </Flex>
            <Modal
                hasHeader
                hasFooter
                contentStyling={{
                    width: '550px',
                    minHeight: '330px',
                    top: {
                        desktop: '0',
                        tablet: '0',
                    },
                }}
                isOpen={openCancelInfoModal}
                onClose={() => setOpenCancelInfoModal(false)}
            >
                <ModalHeader fontSize="lg">
                    Free Cancellation Policy
                </ModalHeader>
                <ModalBody>
                    <Text>
                        {reservation.cancellationMinutes > 0
                            ? `You have up to ${hoursText} before your`
                            : 'At this facility, you have up until the minute your'}{' '}
                        reservation begins to cancel your parking for a full
                        refund. You can cancel your reservation on the SpotHero
                        website or app.
                        <br />
                        <br />
                        If you&apos;re having trouble with your reservation and
                        it&apos;s after the start time, please reach out to our
                        Customer Heroes who are more than happy to help rectify
                        any situation!
                    </Text>
                </ModalBody>
                <ModalFooter>
                    <Button
                        onClick={() => setOpenCancelInfoModal(false)}
                        variant="primary"
                    >
                        Okay
                    </Button>
                </ModalFooter>
            </Modal>
            {reservation.reservationType !==
            ReservationUtils.RESERVATION_TYPES.MONTHLY ? (
                <Modal
                    hasHeader
                    hasFooter
                    size={
                        currentView === INELIGIBLE_VIEW ||
                        (currentView === REFUND_DESTINATION_VIEW &&
                            (onlyOriginalPayment || onlyCredit))
                            ? 'sm'
                            : 'md'
                    }
                    isOpen={isOpen}
                    onClose={handleClose}
                    hideCloseButton={refundReasonIsSubmitting}
                    closeOnOverlayClick={!refundReasonIsSubmitting}
                    data-testid="CancelReservation-modal-cancel"
                >
                    <ModalHeader fontSize="lg">
                        {currentView === INELIGIBLE_VIEW
                            ? null
                            : 'Cancellation Request'}
                    </ModalHeader>
                    <ModalBody
                        width="100%"
                        display={
                            currentView === LOADING_VIEW ? 'flex' : 'block'
                        }
                    >
                        {currentView === LOADING_VIEW && (
                            <Flex
                                alignItems="center"
                                justifyContent="center"
                                width="100%"
                            >
                                <Loader thickness={3} size={12} />
                            </Flex>
                        )}
                        {currentView === REASON_SELECT_VIEW && (
                            <RefundReasons
                                handleSubmit={handleRefundReasonSubmit(
                                    onReasonSubmit
                                )}
                                control={refundReasonControl}
                                fieldId={REFUND_REASON_FIELD}
                                formId={REFUND_REASON_FORM}
                            />
                        )}
                        {currentView === REFUND_DESTINATION_VIEW && (
                            <>
                                <ReservationRefund
                                    handleSubmit={handleRefundDestinationSubmit(
                                        onDestinationSubmit
                                    )}
                                    reservation={reservation}
                                    control={refundDestinationControl}
                                    fieldId={REFUND_DESTINATION_FIELD}
                                    formId={REFUND_DESTINATION_FORM}
                                    register={refundDestinationRegister}
                                    onlyOriginalPayment={onlyOriginalPayment}
                                    onlyCredit={onlyCredit}
                                />
                                {refundError ? (
                                    <Alert
                                        title="Oops, we hit a snag issuing your refund"
                                        description="Please try again."
                                        showIcon
                                        status="error"
                                        size="md"
                                        marginBottom={4}
                                    />
                                ) : null}
                            </>
                        )}
                        {currentView === INELIGIBLE_VIEW && (
                            <Flex alignItems="center" flexFlow="column">
                                <Image
                                    src="/img/parking-pass/detour.svg"
                                    width={24}
                                    height="auto"
                                    marginBottom={6}
                                    marginTop={{base: 4, desktopXL: 0}}
                                />
                                <Text fontSize="base" textAlign="center">
                                    Sorry, the reservation couldn&rsquo;t be
                                    cancelled.
                                </Text>
                            </Flex>
                        )}
                    </ModalBody>
                    <ModalFooter
                        flexDirection={{base: 'column-reverse', tablet: 'row'}}
                        gap={4}
                    >
                        {currentView === INELIGIBLE_VIEW ? (
                            <Button
                                onClick={handleClose}
                                variant="primary"
                                fontFamily="body"
                                width="100%"
                            >
                                Close
                            </Button>
                        ) : (
                            <>
                                <Button
                                    onClick={handleClose}
                                    variant="secondary"
                                    fontFamily="body"
                                    disabled={
                                        refundReasonIsSubmitting ||
                                        refundDestinationIsSubmitting
                                    }
                                    width={{base: '100%', tablet: 'auto'}}
                                >
                                    Cancel
                                </Button>

                                {currentView === REASON_SELECT_VIEW && (
                                    <Button
                                        form={REFUND_REASON_FORM}
                                        type="submit"
                                        variant="primary"
                                        fontFamily="body"
                                        disabled={
                                            refundReasonIsSubmitting ||
                                            !refundReasonDirtyFields[
                                                REFUND_REASON_FIELD
                                            ] ||
                                            currentView === LOADING_VIEW
                                        }
                                        width={{base: '100%', tablet: 'auto'}}
                                    >
                                        Next
                                    </Button>
                                )}

                                {currentView === REFUND_DESTINATION_VIEW && (
                                    <Button
                                        form={REFUND_DESTINATION_FORM}
                                        type="submit"
                                        variant="primary"
                                        fontFamily="body"
                                        disabled={
                                            refundDestinationIsSubmitting ||
                                            (!onlyOriginalPayment &&
                                                !onlyCredit &&
                                                !refundDestinationDirtyFields[
                                                    REFUND_DESTINATION_FIELD
                                                ])
                                        }
                                        width={{
                                            base: '100%',
                                            tablet: 'auto',
                                        }}
                                    >
                                        Submit Cancellation
                                    </Button>
                                )}
                            </>
                        )}
                    </ModalFooter>
                </Modal>
            ) : null}
        </>
    );
};

CancelReservation.propTypes = {
    reservation: PROPTYPE_RESERVATION.isRequired,
    activeStatus: PropTypes.oneOf(Object.values(ReservationUtils.ACTIVE_STATUS))
        .isRequired,
    onTrackedClick: PropTypes.func.isRequired,
};

export default CancelReservation;
